'use client'

import { useCallback, useEffect, useState } from 'react'
import type { MatchState, MatchStatus } from '@/lib/live/state'

export type LiveTransport = 'ws' | 'sse' | 'poll'

export interface LiveSessionData {
  sessionKey: string
  status: MatchStatus
  revision: number
  homeTeam: string
  awayTeam: string
  homeLogo: string | null
  awayLogo: string | null
  scheduledAt: string | null
  state: MatchState
}

interface LiveUpdate {
  revision: number
  status: MatchStatus
  state: MatchState
}

/** Opzioni di `apply`: vedi `applyUpdate`. */
export interface LiveApplyOptions {
  /**
   * Adotta il payload anche a **pari revision**. Serve a chi ha appena scritto
   * quello stato (risposta di `POST /action`): se il WS ha già consegnato la
   * stessa revision, lo stato del server è per costruzione quello appena
   * scritto e non va scartato. Non permette mai di tornare indietro.
   */
  force?: boolean
}

/**
 * Hook client per seguire una sessione live in tempo reale.
 *
 * Strategia di trasporto, in ordine di preferenza:
 *   1. WebSocket (`window.location.host` + `/ws`) sul canale `live:<sessionKey>`;
 *   2. dopo 2 fallimenti di connessione, SSE (`/api/live/[sessionKey]/sse`);
 *   3. polling lento (10s) come ultima risorsa.
 *
 * Lo stato iniziale arriva dal server (primo paint già corretto) e viene
 * aggiornato dai trasporti. `transport` dice quale canale è attivo, così la UI
 * può mostrare lo stato ("diretto" / "riconnessione" / "aggiornamento lento").
 *
 * Garanzie di coerenza (tre trasporti possono sovrapporsi):
 *   - `apply` è **monotona** sulla `revision`: un payload più vecchio non
 *     sovrascrive mai uno più nuovo (punteggio che torna indietro);
 *   - il WS non rigioca gli eventi persi durante un buco di rete: all'ACK
 *     `subscribed` si legge lo stato (`snapshot`), quindi una partita finita
 *     mentre il socket era giù non lascia il viewer stantio;
 *   - in background il polling e l'SSE si fermano (il costo è per spettatore),
 *     al ritorno in primo piano c'è uno snapshot immediato e il trasporto
 *     riparte; il WS, che è push, resta aperto.
 *
 * `apply` è esposta per chi deve iniettare uno stato autoritativo che non
 * arriva dal bus (es. la risposta di `POST /api/live/<key>/action` mentre si
 * scrive, o un riallineamento dopo 409 in `useLiveOperator`).
 */
export function useLiveSession(sessionKey: string, initial: LiveSessionData) {
  const [data, setData] = useState<LiveSessionData>(initial)
  const [connected, setConnected] = useState(false)
  const [transport, setTransport] = useState<LiveTransport>('ws')

  const applyUpdate = useCallback((u: LiveUpdate, opts?: LiveApplyOptions) => {
    if (!u || typeof u.revision !== 'number' || !u.state || typeof u.state !== 'object') return
    const force = opts?.force === true
    setData((prev) => {
      // Mai tornare indietro, nemmeno con `force`: un payload più vecchio è
      // solo un evento in ritardo su un altro trasporto.
      if (u.revision < prev.revision) return prev
      // A pari revision vince il primo arrivato (stato identico per
      // costruzione, la revision è unica per scrittura) — salvo `force`.
      if (u.revision === prev.revision && !force) return prev
      return { ...prev, revision: u.revision, status: u.status, state: u.state }
    })
    setConnected(true)
  }, [])

  useEffect(() => {
    let ws: WebSocket | null = null
    let es: EventSource | null = null
    let poll: ReturnType<typeof setInterval> | null = null
    let sseWatch: ReturnType<typeof setInterval> | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let attempts = 0
    let unmounted = false
    /** Ripiego attivo: `'sse'`/`'poll'`, oppure `null` quando il WS è su. */
    let degraded: 'sse' | 'poll' | null = null
    /**
     * La sessione non è più leggibile (404 su `/state`, oppure `event: terminal`
     * dallo stream): il viewer si ferma e **non** riparte fino al rimontaggio.
     * Senza questo flag un 404 generava una GET ogni 3 s per ogni scheda
     * aperta su una gara nascosta o cancellata.
     */
    let terminal = false
    let lastTerminalProbe = 0

    /** Lettura autoritativa: riallineamento, risveglio, e sonda di terminalità. */
    const snapshot = async (): Promise<'ok' | 'gone' | 'error'> => {
      if (unmounted) return 'error'
      try {
        const r = await fetch(`/api/live/${sessionKey}/state`, { cache: 'no-store' })
        // 404 = la sessione non esiste, o è `visible:false` per un anonimo:
        // non è un guasto transitorio, è «non c'è più niente da seguire».
        if (r.status === 404) return 'gone'
        if (!r.ok) return 'error'
        const j = (await r.json()) as {
          session?: { revision: number; status: MatchStatus; state: MatchState }
        }
        const s = j.session
        if (!s || typeof s.revision !== 'number') return 'error'
        applyUpdate({ revision: s.revision, status: s.status, state: s.state })
        return 'ok'
      } catch {
        /* riprova al prossimo tick o alla prossima riconnessione */
        return 'error'
      }
    }

    const stopPoll = () => {
      if (poll) {
        clearInterval(poll)
        poll = null
      }
    }

    const stopSse = () => {
      if (sseWatch) {
        clearInterval(sseWatch)
        sseWatch = null
      }
      if (es) {
        try {
          es.close()
        } catch {
          /* noop */
        }
        es = null
      }
    }

    /** Ferma tutto, definitivamente: nessun trasporto riparte da solo. */
    const terminate = () => {
      if (terminal) return
      terminal = true
      setConnected(false)
      stopPoll()
      stopSse()
      degraded = null
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
      try {
        ws?.close()
      } catch {
        /* noop */
      }
      ws = null
    }

    /** Il WS è tornato: i ripieghi non servono più (niente doppio canale). */
    const dropFallback = () => {
      stopPoll()
      stopSse()
      degraded = null
    }

    const startPoll = () => {
      if (poll || unmounted || terminal) return
      degraded = 'poll'
      setTransport('poll')
      const tick = async () => {
        // 404: si smette di interrogare, invece di ritentare per sempre.
        if ((await snapshot()) === 'gone') terminate()
      }
      // Primo dato subito: un fallback che aspetta 10 s per dire qualcosa
      // lascia la pagina ferma proprio quando serve.
      void tick()
      poll = setInterval(() => {
        void tick()
      }, 10000)
    }

    const startSse = () => {
      if (es || unmounted || terminal) return
      try {
        es = new EventSource(`/api/live/${sessionKey}/sse`)
        degraded = 'sse'
        setTransport('sse')
        es.onmessage = (ev) => {
          try {
            const u = JSON.parse(ev.data) as LiveUpdate
            if (u && typeof u.revision === 'number') applyUpdate(u)
          } catch {
            /* payload non valido */
          }
        }
        // `event: terminal` = la sessione non è più leggibile. È un evento
        // dedicato e non `error`, altrimenti si confonderebbe con i guasti di
        // connessione del browser.
        es.addEventListener('terminal', () => terminate())
        es.onerror = () => {
          // Il browser ritenta da solo: un 404 sul pre-check non arriva qui con
          // lo status, quindi una lettura di /state dice se continuare a
          // ritentare ha senso. Al massimo una sonda ogni 8 s.
          if (!terminal && Date.now() - lastTerminalProbe > 8000) {
            lastTerminalProbe = Date.now()
            void snapshot().then((res) => {
              if (res === 'gone') terminate()
            })
          }
          // Se invece è caduto il canale, degrada a polling.
          if (sseWatch || terminal) return
          sseWatch = setInterval(() => {
            if (es && es.readyState === EventSource.CLOSED) {
              stopSse()
              startPoll()
            }
          }, 3000)
        }
      } catch {
        startPoll()
      }
    }

    const connectWs = () => {
      if (unmounted || terminal) return
      // Il canale WebSocket vive sullo **stesso host** della pagina: il vhost
      // proxa `/ws` verso il processo live-ws dell'istanza. Derivarlo da
      // `window.location` invece che da un env compilato rende il live corretto
      // su qualunque dominio serva l'istanza (puglia.pallavolo.me, veneto,
      // emiliaromagna, legapuglia.sportnix.me): una variabile `NEXT_PUBLIC_*`
      // sbagliata (in produzione puntava al dominio di **sviluppo**) faceva
      // connettere il socket a un'altra istanza, che risponde — quindi nessun
      // degrado a SSE — e la pagina non si aggiornava mai.
      // Questo hook gira solo nel browser (l'effetto non esiste in SSR):
      // l'unico caso senza `window` è un ambiente non-DOM, dove il WS non ha
      // senso e si passa direttamente al ripiego SSE.
      if (typeof window === 'undefined') {
        startSse()
        return
      }
      const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`

      let socket: WebSocket
      try {
        socket = new WebSocket(wsUrl)
      } catch {
        startSse()
        return
      }
      ws = socket
      // Una WebSocket che **non apre** (proxy che non inoltra l'upgrade) resta in
      // CONNECTING e non emette `onclose`: il viewer non riceve nulla e il
      // ripiego non parte mai. Stessa cosa se apre ma il server non conosce
      // `live:<key>` (vecchio ws-server: risponde `connected` con i canali
      // legacy). In entrambi i casi, dopo 4 s si chiude e si passa a SSE.
      let ackOk = false
      let ackTimer: ReturnType<typeof setTimeout> | null = null
      ackTimer = setTimeout(() => {
        if (ackOk && socket.readyState === WebSocket.OPEN) return
        try { socket.close() } catch { /* già chiusa */ }
        attempts += 1
        startSse()
      }, 4000)

      socket.onopen = () => {
        attempts = 0
        dropFallback()
        setTransport('ws')
        setConnected(true)
        socket.send(JSON.stringify({ action: 'subscribe', channel: `live:${sessionKey}` }))
      }

      socket.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data) as {
            type?: string
            sessionKey?: string
            channel?: string
            payload?: LiveUpdate
          }
          if (msg.type === 'live_update' && msg.sessionKey === sessionKey && msg.payload) {
            applyUpdate(msg.payload)
            return
          }
          // ACK della sottoscrizione: non porta stato. Il server non rigioca
          // gli eventi persi, quindi ci si riallinea leggendo lo stato: senza
          // questo, una partita chiusa durante un buco di rete lascia il
          // viewer stantio per sempre (SSE e polling si riallineano da soli,
          // il WS no).
          if (msg.type === 'subscribed' && (!msg.channel || msg.channel === `live:${sessionKey}`)) {
            ackOk = true
            if (ackTimer) { clearTimeout(ackTimer); ackTimer = null }
            void snapshot()
          }
        } catch {
          /* payload non valido */
        }
      }

      socket.onclose = () => {
        if (ackTimer) { clearTimeout(ackTimer); ackTimer = null }
        setConnected(false)
        if (unmounted || terminal) return
        attempts += 1
        if (attempts >= 2) {
          // Ripiego immediato, ma il WS resta il trasporto più economico:
          // continuiamo a sondarlo, e se torna su `onopen` ferma SSE/poll.
          startSse()
          if (!reconnectTimer) {
            reconnectTimer = setTimeout(() => {
              reconnectTimer = null
              connectWs()
            }, 30000)
          }
          return
        }
        const delay = Math.min(1000 * 2 ** attempts, 8000)
        if (reconnectTimer) return
        reconnectTimer = setTimeout(() => {
          reconnectTimer = null
          connectWs()
        }, delay)
      }

      socket.onerror = () => {
        /* onclose seguirà */
      }
    }

    /**
     * Scheda in background: si ferma tutto ciò che costa per spettatore (poll
     * e SSE, che sul server interroga Postgres ogni 1,5 s). Il WS resta: è una
     * connessione push e non aggiunge query.
     */
    const onVisibility = () => {
      if (unmounted || terminal) return
      if (document.visibilityState === 'hidden') {
        stopPoll()
        stopSse()
        return
      }
      // Ritorno visibile: lo stato può essere cambiato mentre non guardavamo.
      // Con il poll è `startPoll` a fare subito lo snapshot (uno solo); con
      // WS/SSE il canale non rigioca gli eventi persi, quindi lo si legge qui.
      if (degraded === 'poll') {
        startPoll()
        return
      }
      void snapshot()
      if (degraded === 'sse') startSse()
    }

    document.addEventListener('visibilitychange', onVisibility)
    connectWs()

    return () => {
      unmounted = true
      document.removeEventListener('visibilitychange', onVisibility)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      stopSse()
      stopPoll()
      try {
        ws?.close()
      } catch {
        /* noop */
      }
    }
  }, [sessionKey, applyUpdate])

  return { data, connected, transport, apply: applyUpdate }
}
